/**
 * CommissionsPage — commission rules management and entry ledger.
 *
 * Two tabs:
 * 1. "Reglas de Comision" — table of commission rules with "Crear Regla" button
 * 2. "Entradas de Comision" — commission entry ledger with status filter tabs
 *    and Aprobar / Rechazar / Marcar Pagado action buttons
 *
 * Status filter tabs for entries: Todos | Pendientes | Aprobadas | Pagadas | Rechazadas
 *
 * Commission amounts displayed as formatted currency (cents → $X.XX)
 * Rate basis points displayed as percentage (1000 bps → 10.00%)
 */

import { useState } from 'react'
import {
  useCommissionRules,
  useCommissionEntries,
  useCreateCommissionRule,
  useApproveCommission,
  usePayCommission,
  useRejectCommission,
  type CommissionRuleRow,
  type CommissionEntryRow,
  type CreateCommissionRuleDTO,
  type CommissionEntryFilters,
} from '@/hooks/useCommissions'

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Format integer cents to display currency string. */
function formatCents(cents: number): string {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents)
}

/** Format basis points to a human-readable percentage string. */
function formatBasisPoints(bps: number): string {
  return `${(bps / 100).toFixed(2)}%`
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

// ── Status badge ──────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  pending:  'bg-yellow-100 text-yellow-800',
  approved: 'bg-blue-100 text-blue-800',
  paid:     'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
}

const STATUS_LABELS: Record<string, string> = {
  pending:  'Pendiente',
  approved: 'Aprobada',
  paid:     'Pagada',
  rejected: 'Rechazada',
}

function StatusBadge({ status }: { status: string }) {
  const color = STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-600'
  const label = STATUS_LABELS[status] ?? status
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${color}`}
    >
      {label}
    </span>
  )
}

// ── Create Rule Form ──────────────────────────────────────────────────────────

interface CreateRuleFormProps {
  onSubmit: (data: CreateCommissionRuleDTO) => Promise<void>
  onCancel: () => void
  isSubmitting: boolean
  error: string | null
}

function CreateRuleForm({ onSubmit, onCancel, isSubmitting, error }: CreateRuleFormProps) {
  const [name, setName] = useState('')
  const [channel, setChannel] = useState<'reseller' | 'freelance'>('reseller')
  const [rateBasisPoints, setRateBasisPoints] = useState('1000')
  const [appliesTo, setAppliesTo] = useState('all')
  const [minAmountCents, setMinAmountCents] = useState('0')
  const [maxAmountCents, setMaxAmountCents] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit({
      name: name.trim(),
      channel,
      rateBasisPoints: parseInt(rateBasisPoints, 10),
      appliesTo: appliesTo.trim() || 'all',
      minAmountCents: parseInt(minAmountCents, 10) || 0,
      maxAmountCents: maxAmountCents ? parseInt(maxAmountCents, 10) : null,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      <div>
        <label htmlFor="cr-name" className="mb-1 block text-sm font-medium text-gray-700">
          Nombre <span className="text-red-500">*</span>
        </label>
        <input
          id="cr-name"
          type="text"
          required
          maxLength={255}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ej. Comision revendedor estandar 10%"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="cr-channel" className="mb-1 block text-sm font-medium text-gray-700">
            Canal <span className="text-red-500">*</span>
          </label>
          <select
            id="cr-channel"
            value={channel}
            onChange={(e) => setChannel(e.target.value as 'reseller' | 'freelance')}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="reseller">Revendedor</option>
            <option value="freelance">Freelancer</option>
          </select>
        </div>

        <div>
          <label htmlFor="cr-bps" className="mb-1 block text-sm font-medium text-gray-700">
            Tasa (basis points) <span className="text-red-500">*</span>
          </label>
          <input
            id="cr-bps"
            type="number"
            required
            min={1}
            value={rateBasisPoints}
            onChange={(e) => setRateBasisPoints(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <p className="mt-1 text-xs text-gray-400">
            1000 = 10%, 500 = 5%, 100 = 1%
          </p>
        </div>
      </div>

      <div>
        <label htmlFor="cr-applies-to" className="mb-1 block text-sm font-medium text-gray-700">
          Aplica a (plan slug o 'all')
        </label>
        <input
          id="cr-applies-to"
          type="text"
          maxLength={255}
          value={appliesTo}
          onChange={(e) => setAppliesTo(e.target.value)}
          placeholder="all"
          className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <p className="mt-1 text-xs text-gray-400">
          'all' aplica a todos los planes. Un slug especifico prioriza sobre 'all'.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="cr-min" className="mb-1 block text-sm font-medium text-gray-700">
            Monto minimo (centavos)
          </label>
          <input
            id="cr-min"
            type="number"
            min={0}
            value={minAmountCents}
            onChange={(e) => setMinAmountCents(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label htmlFor="cr-max" className="mb-1 block text-sm font-medium text-gray-700">
            Monto maximo (centavos, vacio = ilimitado)
          </label>
          <input
            id="cr-max"
            type="number"
            min={1}
            value={maxAmountCents}
            onChange={(e) => setMaxAmountCents(e.target.value)}
            placeholder="Sin limite"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? 'Creando...' : 'Crear regla'}
        </button>
      </div>
    </form>
  )
}

// ── Reject Dialog ─────────────────────────────────────────────────────────────

interface RejectDialogProps {
  entryId: string
  onConfirm: (reason: string) => Promise<void>
  onCancel: () => void
  isSubmitting: boolean
}

function RejectDialog({ onConfirm, onCancel, isSubmitting }: RejectDialogProps) {
  const [reason, setReason] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!reason.trim()) return
    await onConfirm(reason.trim())
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Rechazar entrada</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="reject-reason" className="mb-1 block text-sm font-medium text-gray-700">
              Razon del rechazo <span className="text-red-500">*</span>
            </label>
            <textarea
              id="reject-reason"
              required
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explica por que se rechaza esta comision..."
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
            />
          </div>
          <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !reason.trim()}
              className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? 'Rechazando...' : 'Rechazar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Commission Rules Table ─────────────────────────────────────────────────────

interface CommissionRulesTableProps {
  rules: CommissionRuleRow[]
}

function CommissionRulesTable({ rules }: CommissionRulesTableProps) {
  if (rules.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500">No hay reglas de comision definidas</p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Nombre
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Canal
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Tasa
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Aplica a
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Monto min / max
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Activa
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Creada
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {rules.map((rule) => (
            <tr key={rule.id} className="hover:bg-gray-50">
              <td className="px-6 py-4">
                <div className="text-sm font-medium text-gray-900">{rule.name}</div>
                <div className="text-xs text-gray-400 font-mono">{rule.id.slice(0, 8)}…</div>
              </td>
              <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700 capitalize">
                {rule.channel === 'reseller' ? 'Revendedor' : 'Freelancer'}
              </td>
              <td className="whitespace-nowrap px-6 py-4 text-sm font-semibold text-gray-900">
                {formatBasisPoints(rule.rate_basis_points)}
              </td>
              <td className="whitespace-nowrap px-6 py-4 text-sm font-mono text-gray-600">
                {rule.applies_to}
              </td>
              <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                {formatCents(rule.min_amount_cents)} / {rule.max_amount_cents != null ? formatCents(rule.max_amount_cents) : '∞'}
              </td>
              <td className="whitespace-nowrap px-6 py-4">
                {rule.active ? (
                  <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                    Activa
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                    Inactiva
                  </span>
                )}
              </td>
              <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                {formatDate(rule.created_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Commission Entries Table ───────────────────────────────────────────────────

interface CommissionEntriesTableProps {
  entries: CommissionEntryRow[]
  onApprove: (id: string) => void
  onPay: (id: string) => void
  onReject: (id: string) => void
  actionPendingId: string | null
}

function CommissionEntriesTable({
  entries,
  onApprove,
  onPay,
  onReject,
  actionPendingId,
}: CommissionEntriesTableProps) {
  if (entries.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500">No hay entradas de comision</p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Socio
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Monto factura
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Comision
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Estado
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Creada
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Acciones
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {entries.map((entry) => {
            const isActionPending = actionPendingId === entry.id
            return (
              <tr key={entry.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900">
                    {entry.user_name ?? entry.user_id.slice(0, 8) + '…'}
                  </div>
                  {entry.user_email && (
                    <div className="text-xs text-gray-400">{entry.user_email}</div>
                  )}
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                  {formatCents(entry.invoice_amount_cents)}
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm font-semibold text-gray-900">
                  {formatCents(entry.amount_cents)}
                </td>
                <td className="whitespace-nowrap px-6 py-4">
                  <StatusBadge status={entry.status} />
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                  {formatDate(entry.created_at)}
                </td>
                <td className="whitespace-nowrap px-6 py-4">
                  <div className="flex items-center gap-2">
                    {entry.status === 'pending' && (
                      <>
                        <button
                          onClick={() => onApprove(entry.id)}
                          disabled={isActionPending}
                          className="rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Aprobar
                        </button>
                        <button
                          onClick={() => onReject(entry.id)}
                          disabled={isActionPending}
                          className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Rechazar
                        </button>
                      </>
                    )}
                    {entry.status === 'approved' && (
                      <button
                        onClick={() => onPay(entry.id)}
                        disabled={isActionPending}
                        className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Marcar Pagado
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ── Page component ────────────────────────────────────────────────────────────

type MainTab = 'rules' | 'entries'
type EntryStatusFilter = '' | 'pending' | 'approved' | 'paid' | 'rejected'

const ENTRY_STATUS_TABS: { value: EntryStatusFilter; label: string }[] = [
  { value: '',         label: 'Todos' },
  { value: 'pending',  label: 'Pendientes' },
  { value: 'approved', label: 'Aprobadas' },
  { value: 'paid',     label: 'Pagadas' },
  { value: 'rejected', label: 'Rechazadas' },
]

export function CommissionsPage() {
  const [mainTab, setMainTab] = useState<MainTab>('rules')
  const [entryStatusFilter, setEntryStatusFilter] = useState<EntryStatusFilter>('')
  const [showCreateRuleModal, setShowCreateRuleModal] = useState(false)
  const [createRuleError, setCreateRuleError] = useState<string | null>(null)

  // Reject dialog state
  const [rejectEntryId, setRejectEntryId] = useState<string | null>(null)
  const [actionPendingId, setActionPendingId] = useState<string | null>(null)

  // Queries
  const { data: rules = [], isLoading: rulesLoading, isError: rulesError } = useCommissionRules()

  const entryFilters: CommissionEntryFilters = {}
  if (entryStatusFilter) entryFilters.status = entryStatusFilter

  const {
    data: entries = [],
    isLoading: entriesLoading,
    isError: entriesError,
  } = useCommissionEntries(entryFilters)

  // Mutations
  const createRuleMutation = useCreateCommissionRule()
  const approveMutation = useApproveCommission()
  const payMutation = usePayCommission()
  const rejectMutation = useRejectCommission()

  // Handlers
  const handleCreateRule = async (data: CreateCommissionRuleDTO) => {
    setCreateRuleError(null)
    try {
      await createRuleMutation.mutateAsync(data)
      setShowCreateRuleModal(false)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al crear la regla'
      setCreateRuleError(message)
    }
  }

  const handleApprove = async (id: string) => {
    setActionPendingId(id)
    try {
      await approveMutation.mutateAsync({ id })
    } catch (err) {
      console.error('[CommissionsPage] Approve failed:', err)
    } finally {
      setActionPendingId(null)
    }
  }

  const handlePay = async (id: string) => {
    setActionPendingId(id)
    try {
      await payMutation.mutateAsync({ id })
    } catch (err) {
      console.error('[CommissionsPage] Pay failed:', err)
    } finally {
      setActionPendingId(null)
    }
  }

  const handleRejectConfirm = async (reason: string) => {
    if (!rejectEntryId) return
    setActionPendingId(rejectEntryId)
    try {
      await rejectMutation.mutateAsync({ id: rejectEntryId, reason })
      setRejectEntryId(null)
    } catch (err) {
      console.error('[CommissionsPage] Reject failed:', err)
    } finally {
      setActionPendingId(null)
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Comisiones</h1>
        {mainTab === 'rules' && (
          <button
            onClick={() => {
              setCreateRuleError(null)
              setShowCreateRuleModal(true)
            }}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Crear regla
          </button>
        )}
      </div>

      {/* Main tab bar */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex gap-6" aria-label="Main tabs">
          <button
            onClick={() => setMainTab('rules')}
            className={`whitespace-nowrap pb-3 text-sm font-medium transition-colors ${
              mainTab === 'rules'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Reglas de Comision
          </button>
          <button
            onClick={() => setMainTab('entries')}
            className={`whitespace-nowrap pb-3 text-sm font-medium transition-colors ${
              mainTab === 'entries'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Entradas de Comision
          </button>
        </nav>
      </div>

      {/* Rules tab */}
      {mainTab === 'rules' && (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          {rulesLoading && (
            <div className="flex items-center justify-center py-12">
              <p className="text-gray-500">Cargando reglas...</p>
            </div>
          )}
          {rulesError && (
            <div className="flex items-center justify-center py-12">
              <p className="text-red-500">Error al cargar las reglas de comision</p>
            </div>
          )}
          {!rulesLoading && !rulesError && <CommissionRulesTable rules={rules} />}
        </div>
      )}

      {/* Entries tab */}
      {mainTab === 'entries' && (
        <div>
          {/* Status filter tabs */}
          <div className="mb-4 flex gap-2 flex-wrap">
            {ENTRY_STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setEntryStatusFilter(tab.value)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  entryStatusFilter === tab.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            {entriesLoading && (
              <div className="flex items-center justify-center py-12">
                <p className="text-gray-500">Cargando entradas...</p>
              </div>
            )}
            {entriesError && (
              <div className="flex items-center justify-center py-12">
                <p className="text-red-500">Error al cargar las entradas de comision</p>
              </div>
            )}
            {!entriesLoading && !entriesError && (
              <CommissionEntriesTable
                entries={entries}
                onApprove={handleApprove}
                onPay={handlePay}
                onReject={(id) => setRejectEntryId(id)}
                actionPendingId={actionPendingId}
              />
            )}
          </div>
        </div>
      )}

      {/* Create rule modal */}
      {showCreateRuleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Nueva regla de comision</h2>
            </div>
            <div className="p-6">
              <CreateRuleForm
                onSubmit={handleCreateRule}
                onCancel={() => setShowCreateRuleModal(false)}
                isSubmitting={createRuleMutation.isPending}
                error={createRuleError}
              />
            </div>
          </div>
        </div>
      )}

      {/* Reject dialog */}
      {rejectEntryId && (
        <RejectDialog
          entryId={rejectEntryId}
          onConfirm={handleRejectConfirm}
          onCancel={() => setRejectEntryId(null)}
          isSubmitting={rejectMutation.isPending}
        />
      )}
    </div>
  )
}
